import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { LOAN_PRODUCTS } from "@/lib/loanProducts";
import { ArrowLeft, FileDown, CreditCard } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const productTitle = (key) => LOAN_PRODUCTS.find((p) => p.key === key)?.title || key;

export default function LoanDetail() {
  const { id } = useParams();
  const [loan, setLoan] = useState(null);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    api.get(`/loans/${id}`).then(({ data }) => {
      setLoan(data);
      // Confirm any pending Paynow payments (e.g. after returning from checkout)
      api.post(`/loans/${id}/poll-pending`).then(({ data: d }) => { if (d.loan) setLoan(d.loan); }).catch(() => {});
    }).catch(() => setLoan(false));
  }, [id]);

  const payInstallment = async (installmentNo) => {
    setPaying(installmentNo);
    try {
      const { data } = await api.post(`/loans/${id}/pay`, { installment_no: installmentNo });
      const p = data.payment;
      if (p.redirect_url) {
        window.location.assign(p.redirect_url); // live Paynow checkout
        return;
      }
      if (p.status === "paid") {
        toast.success(`Installment ${installmentNo} paid${data.demo_mode ? " (demo)" : ""}.`);
        if (data.loan) setLoan(data.loan);
      } else {
        toast.error(`Payment ${p.status}${p.error ? `: ${p.error}` : ""}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPaying(null);
    }
  };

  if (loan === null) return <DashboardLayout role="client"><div>Loading…</div></DashboardLayout>;
  if (loan === false) return <DashboardLayout role="client"><div>Loan not found.</div></DashboardLayout>;

  const pct = Math.min(100, (loan.amount_paid / loan.total_repayable) * 100);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <DashboardLayout role="client">
      <Link to="/dashboard/loans" className="mb-4 inline-flex items-center gap-2 text-sm text-ink-soft hover:text-[#5B2C82]" data-testid="loan-back-link">
        <ArrowLeft className="h-4 w-4" /> All loans
      </Link>

      <div className="rounded-xl border border-[#E5E2DE] bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-ink">{productTitle(loan.product)}</h1>
            <div className="text-xs text-ink-muted">Loan #{loan.id.slice(0, 8)} · Disbursed {new Date(loan.disbursed_on).toLocaleDateString()}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/loans/${loan.id}/agreement.pdf?auth=${localStorage.getItem("jrc_token") || ""}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#5B2C82] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#46225f]"
              data-testid="client-download-agreement"
            >
              <FileDown className="h-3.5 w-3.5" /> Download agreement
            </a>
            <span className={`rounded-full border px-3 py-1 text-xs ${loan.status === "active" ? "border-emerald-300 bg-emerald-100 text-emerald-900" : "border-violet-300 bg-violet-100 text-violet-900"}`}>
              {loan.status}
            </span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Principal" value={fmt(loan.principal)} />
          <Stat label="Interest rate" value={`${loan.interest_rate_pct}%`} />
          <Stat label="Term" value={`${loan.term_months} mo`} />
          <Stat label="Total repayable" value={fmt(loan.total_repayable)} />
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-ink">{fmt(loan.amount_paid)} repaid</span>
            <span className="text-ink-soft">{fmt(loan.balance)} remaining</span>
          </div>
          <Progress value={pct} className="mt-2 h-2" />
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-[#E5E2DE] bg-white">
        <div className="border-b border-[#E5E2DE] px-5 py-3">
          <h2 className="font-heading text-lg font-semibold text-ink">Repayment schedule</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F6F0] text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Due date</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Principal</th>
                <th className="px-4 py-3 text-right">Interest</th>
                <th className="px-4 py-3 text-right">Balance after</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Pay</th>
              </tr>
            </thead>
            <tbody>
              {loan.schedule.map((inst) => {
                const overdue = inst.status !== "paid" && inst.due_date < today;
                return (
                  <tr key={inst.installment_no} className="border-t border-[#E5E2DE]" data-testid={`schedule-row-${inst.installment_no}`}>
                    <td className="px-4 py-3 text-ink-muted">{inst.installment_no}</td>
                    <td className="px-4 py-3 text-ink">{inst.due_date}</td>
                    <td className="px-4 py-3 text-right font-semibold text-ink">{fmt(inst.amount_due)}</td>
                    <td className="px-4 py-3 text-right text-ink-soft">{fmt(inst.principal)}</td>
                    <td className="px-4 py-3 text-right text-ink-soft">{fmt(inst.interest)}</td>
                    <td className="px-4 py-3 text-right text-ink-soft">{fmt(inst.balance_after)}</td>
                    <td className="px-4 py-3">
                      {inst.status === "paid" ? (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-900">Paid {inst.paid_on}</span>
                      ) : overdue ? (
                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs text-rose-900">Overdue</span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {inst.status !== "paid" && (
                        <Button
                          size="sm"
                          onClick={() => payInstallment(inst.installment_no)}
                          disabled={paying !== null}
                          className="h-8 bg-[#E87722] px-3 text-xs text-white hover:bg-[#cc6517]"
                          data-testid={`pay-installment-${inst.installment_no}`}
                        >
                          <CreditCard className="mr-1 h-3.5 w-3.5" />
                          {paying === inst.installment_no ? "Processing…" : "Pay now"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-0.5 font-heading text-lg font-bold text-ink">{value}</div>
    </div>
  );
}
